import { 
    Firestore, 
    collection, 
    getDocs, 
    doc,
    writeBatch,
    DocumentSnapshot,
    Timestamp
  } from 'firebase/firestore';
  import { Status } from '../types';
  
  interface StatusGroup {
    name: string;
    documents: {
      id: string;
      data: Status;
      createTime?: Date;
    }[];
  }
  
  export const cleanupStatuses = async (firestore: Firestore, workspaceId: string): Promise<void> => {
    console.log('Starting status cleanup...');
    const statusesRef = collection(firestore, `workspaces/${workspaceId}/statuses`);
    
    try {
      // Get all statuses
      const snapshot = await getDocs(statusesRef);
      
      // Group by name
      const statusGroups: Record<string, StatusGroup> = {};
      
      snapshot.forEach((document: DocumentSnapshot) => {
        const data = document.data() as Status;
        const createTime = (document.get('createdAt') as Timestamp)?.toDate() || new Date();
        
        if (!statusGroups[data.name]) {
          statusGroups[data.name] = {
            name: data.name,
            documents: []
          };
        }
        
        statusGroups[data.name].documents.push({
          id: document.id,
          data,
          createTime
        });
      });
      
      // For each group with duplicates, keep the oldest one and delete the rest
      const batch = writeBatch(firestore);
      let deletionsCount = 0;
      
      for (const group of Object.values(statusGroups)) {
        if (group.documents.length > 1) {
          console.log(`Found ${group.documents.length} copies of status: ${group.name}`);
          
          // Sort by creation time if available, otherwise keep first one
          group.documents.sort((a, b) => {
            if (a.createTime && b.createTime) {
              return a.createTime.getTime() - b.createTime.getTime();
            }
            return 0;
          });
          
          // Keep the first one, delete the rest
          const toKeep = group.documents[0];
          const toDelete = group.documents.slice(1);
          
          console.log(`Keeping ${group.name} with ID: ${toKeep.id}`);
          
          for (const document of toDelete) {
            batch.delete(doc(statusesRef, document.id));
            deletionsCount++;
          }
        }
      }
      
      if (deletionsCount > 0) {
        await batch.commit();
        console.log(`Successfully removed ${deletionsCount} duplicate statuses`);
      } else {
        console.log('No duplicates found');
      }
      
    } catch (error) {
      console.error('Error during status cleanup:', error);
      throw error;
    }
  };
  
  // Helper function to run the cleanup
  export const runStatusCleanup = async (workspaceId: string): Promise<void> => {
    try {
      const { getFirestore } = await import('firebase/firestore');
      const firestore = getFirestore();
      await cleanupStatuses(firestore, workspaceId);
    } catch (error) {
      console.error('Failed to run status cleanup:', error);
      throw error;
    }
  };